import { FavouriteRowValue, FavouriteDataColumn, FavouriteDataModel, SortOrderType, ColumnDataType } from './favourite-interfaces';
import { LayoutConfig } from './layout-config';
import { ColumnType, Period } from '../../constants';

import findIndex from 'lodash/findindex';
import isString from 'lodash/isstring';
import isNumber from 'lodash/isnumber';
import isFinite from 'lodash/isfinite';
import isDate from 'lodash/isdate';
import orderBy from 'lodash/orderBy';
import { CodetableFeature } from 'app/area/codetable/types-store';

interface ColumnExtreme { min: any; max: any; sum: number }
interface CheckColumnType { col: FavouriteDataColumn; extreme: ColumnExtreme }

export interface ColumnMetaData {
  sortOrderType: SortOrderType;
  hasSequence: boolean;
  hasIcon: boolean;
  hasBgColour: boolean;
  hasFgColour: boolean;
}

export class FavouriteDataAdapter {
  public layoutConfig: LayoutConfig;
  public dataModel: FavouriteDataModel;

  public columnMetaData: ColumnMetaData[];
  public columnCategories: FavouriteRowValue[][];
  public columnExtremes: ColumnExtreme[];
  public aggrColIndex: number = -1;
  public aggrRollAvgColIndex: number = -1;
  public targetColIndex: number = -1;
  public uomColIndex: number = -1;
  public selectionColIndex: number = -1;
  public selectionCount: number = 0;
  public compactColumnIndex: number = -1;
  public indexesToCompare: number[] = [];

  public hasRollingAverage = () => this.aggrRollAvgColIndex >= 0;
  public hasTarget = () => this.targetColIndex >= 0;

  public static numRegex: RegExp = /^\d+/;
  private noValueText: string;
  private formatters: [(num: number) => string] | [] = [];
  public intl: any;
  public translation: any;
  public showPercentageInTooltip: boolean = false;
  public numberOfGroupByFields: number = 0;
  public setChartWarningMessage: (message: string) => void;

  constructor(config: LayoutConfig, dataModel: FavouriteDataModel, intl: any, translation: any) {
    this.layoutConfig = config;
    this.dataModel = dataModel;
    this.noValueText = config.text('NoValue', '-- No value --');
    this.intl = intl;
    this.translation = translation;
    this.setupForDataModel();
  }

  private setupForDataModel() {
    if (!this.dataModel || !this.dataModel.columns) {
      return;
    }

    // Prepare formatters for numeric and date columns, to be used later    
    const categories: FavouriteRowValue[][] = [];
    const extremes: ColumnExtreme[] = [];
    const checkColumnType: CheckColumnType[] = [];
    this.columnMetaData = [];

    this.prepareColumns(this.dataModel, categories, extremes, checkColumnType);

    // Handle aggregation column uom (if there is one)
    const uomDomain = this.dataModel.columns[this.aggrColIndex].uomDomain;
    if (uomDomain > 0) {
      const uomColIndex = findIndex(this.dataModel.columns, (col: FavouriteDataColumn) => col.domain === uomDomain); //eslint:disable-line
      if (uomColIndex >= 0) {
        this.uomColIndex = uomColIndex;
      }
    }

    for (let r = 0; r < this.dataModel.rows.length; r++) {
      // position us down in the structure, and place the value object
      for (let c = 0; c < this.dataModel.columns.length; c++) {
        const colValue = this.dataModel.rows[r][c];
        if (!colValue.hasOwnProperty('value')) {
          colValue.value = null;
        }
        if (colValue.sequence || colValue.aggrSequence) {
          this.columnMetaData[c].hasSequence = true;
        }
        if (colValue.iconUrl) {
          this.columnMetaData[c].hasIcon = true;
        }
        if (colValue.bgColour) {
          this.columnMetaData[c].hasBgColour = true;
        }
        if (colValue.fgColour) {
          this.columnMetaData[c].hasFgColour = true;
        }

        const colCategories = categories[c];

        if (!colCategories.find(x => x.value === colValue.value)) {
          colCategories.push(colValue);
        }
        if (extremes[c].min == null || (extremes[c].min > colValue.value && colValue.value !== null)) {
          extremes[c].min = colValue.value;
        }
        if (extremes[c].max == null || (extremes[c].max < colValue.value && colValue.value !== null)) {
          extremes[c].max = colValue.value;
        }
        if (c === this.aggrColIndex && isFinite(+colValue.value)) {
          extremes[c].sum += colValue.value;
        }
      }
    }

    for (let i = 0; i < checkColumnType.length; i++) {
      if (checkColumnType[i].extreme.max < 1) {
        checkColumnType[i].col = {
          ...checkColumnType[i].col,
          dataType: 'Float'
        };
      }
    }

    // Order categories
    for (let c = 0; c < this.dataModel.columns.length; c++) {
      const colCategories = categories[c];
      const colOrderBy = this.dataModel.columns[c].orderBy || [];

      if (colOrderBy?.length > 0) {
        const sortOrderType = this.dataModel.columns[c].sortOrderType || SortOrderType.Asc;

        // Map null to empty string so it appears first in asc and last in desc order.
        const stringTypeOrderByFuncs = colOrderBy.map(
          cn => (o: FavouriteRowValue) => cn !== CodetableFeature.sequence ? o[cn]?.toString().toLowerCase() ?? '' : o[cn]
        );

        // Map null to empty string so it appears first in asc and last in desc order.
        const numberTypeOrderByFuncs = colOrderBy.map(
          cn => (o: FavouriteRowValue) => o[cn] ?? ''
        );

        const columnDataType = this.dataModel.columns[c].dataType;

        let sortedCategories: FavouriteRowValue[];

        switch (columnDataType) {
          case ColumnDataType.Interval:
            sortedCategories = colCategories;
            break;
          case ColumnDataType.Integer:
          case ColumnDataType.Float:
            sortedCategories = orderBy(colCategories, numberTypeOrderByFuncs, [this.getSortOrder(sortOrderType)]);
            break;
          default:
            sortedCategories = orderBy(colCategories, stringTypeOrderByFuncs, [this.getSortOrder(sortOrderType)]);
        }

        categories[c] = sortedCategories;
      }
    }

    this.columnCategories = categories;
    this.columnExtremes = extremes;

    // prepare table columns
    const tableIndexes = this.dataModel.columns
      .filter((column, index) => column.columnType === ColumnType.selection && index !== this.uomColIndex)
      .map((column, index) => index);

    this.compactColumnIndex = tableIndexes[tableIndexes.length - 1];
    this.indexesToCompare = tableIndexes.slice(0, -1);
  }

  private getSortOrder(sortOrderType: SortOrderType) {
    switch (sortOrderType) {
      case SortOrderType.Asc:
        return 'asc';
      case SortOrderType.Desc:
        return 'desc';
      default:
        return 'asc';
    }
  }

  private prepareColumns(
    dataModel: FavouriteDataModel,
    categories: FavouriteRowValue[][],
    extremes: ColumnExtreme[],
    checkColumnType: CheckColumnType[]
  ) {
    for (let i = 0; i < dataModel.columns.length; i++) {
      const column = dataModel.columns[i];
      const colExtremes = { min: null, max: null, sum: 0, direction: 0 };

      categories.push([]);
      extremes.push(colExtremes);

      this.columnMetaData.push(
        {
          hasSequence: false,
          hasIcon: false,
          hasBgColour: false,
          hasFgColour: false,
          sortOrderType: column.sortOrderType
        }
      );

      // somethimes get datatype ='Float' and sometimes get datatype ='Date' for same searching ,when select day or weekday
      const isAggrTargetOrRollAvg = this.isAggrColumn(dataModel.columns, i);
      const isSelectionAndFloat = (column.dataType === 'Float' && column.columnType === 'selection');
      if (column.columnType === 'selection') {
        this.selectionCount++;
      }

      if (column.columnType === 'target') {
        this.formatters[i] = this.buildNumberFormatter(this.intl, true, 2);
      } else if ((column.dataType === 'Float' &&
        !column.interval && !column.codetable) || (column.dataType === '' && isAggrTargetOrRollAvg)) {
        // We use digit grouping only for aggregate columns. Max decimals is always taken from the config.
        this.formatters[i] = this.buildNumberFormatter(
          this.intl, isAggrTargetOrRollAvg || isSelectionAndFloat, column.numberOfDecimals || 3
        );
      } else if (column.dataType === 'Interval') {
        const formatIfFiniteNumber = (v: string) => {
          const formatter = this.buildNumberFormatter(
            this.intl, true, column.numberOfDecimals || 3
          );
          if (formatter && isFinite(Number(v))) {
            return formatter(Number(v));
          } else {
            return v;
          }
        };
        const formatIntervalValue = (val: number): string => val.toString().split(' - ').map(formatIfFiniteNumber).join(' - ');
        this.formatters[i] = (val: number) => formatIntervalValue(val);
      } else if ((column.dataType === 'Integer' || column.dataType === 'BigInteger') && !column.interval && !column.codetable) {
        let maxDecimals = 0;
        if (column.aggregationType === 3) { // Average
          maxDecimals = 1;
        }
        else if (dataModel.report === 10) { // Frequency report
          maxDecimals = column.numberOfDecimals !== null ? column.numberOfDecimals : 1;
          if (maxDecimals > 1) {
            checkColumnType.push({ col: column, extreme: colExtremes });
          }
        }
        this.formatters[i] = this.buildNumberFormatter(this.intl, isAggrTargetOrRollAvg || isSelectionAndFloat, maxDecimals);
      } else if (column.dataType === 'Date') {
        const period = column.period;
        const selectionType = column.selectionType;
        this.formatters[i] = this.buildDateFormatter(this.intl, selectionType, period);
      } else {
        this.formatters[i] = (v: any) => v.toString();
      } // NOOP formatter

      if (column.columnType === ColumnType.aggregationRollAvg) {
        this.aggrRollAvgColIndex = i;
      } else if (column.columnType === ColumnType.target) {
        this.targetColIndex = i;
      } else if (column.columnType === ColumnType.aggregation) {
        this.aggrColIndex = i;
      } else if (column.columnType === ColumnType.selection) {
        this.selectionColIndex = i;
      }
    }
  }

  private buildDateFormatter(intl: any, selectionType: number, period: Period) {
    const dateFormatOptions = period ?
      this.buildDateFormatOptionsFromPeriod(period) :
      { timeZone: 'UTC' };

    return (d: any) => {
      if (isString(d)) {
        return d; // week numbers are now converted to real dates. Treat as string.
      }

      if (isNumber(d)) {
        d = new Date(d);
      }

      if (isDate(d)) {

        // Period
        if (selectionType === 3) {
          if (period === Period.Quarter) {

            const quarter = Math.floor(((d as Date).getUTCMonth() + 3) / 3);
            return intl.formatNumber(quarter) + ' ' + intl.formatDate(d, dateFormatOptions);
          }

          return intl.formatDate(d, dateFormatOptions);
        }
        else {
          // Date and time
          return intl.formatDate(d, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZone: 'UTC'
          });
        }
      }

      return () => d;
    };
  }

  public buildNumberFormatter(
    intl: any, useDigitGrouping: boolean, maxDecimals: number, style: string = 'decimal'
  ): (num: number) => string {
    const options = {
      style: style,
      useGrouping: useDigitGrouping,
      minimumFractionDigits: 0,
      maximumFractionDigits: maxDecimals
    };

    return (num: number) => {
      if (style === 'percent') { // the silly Intl formatters expect a number between 0 and 1 when formatting percent
        num = num / 100;
      }
      return intl.formatNumber(num, options);
    };
  }

  private buildDateFormatOptionsFromPeriod(period: Period) {
    return {
      year: 'numeric',
      month: period === Period.Month
        ? 'short'
        : period === Period.Day ? 'numeric' : undefined,
      day: period === Period.Day ? 'numeric' : undefined,
      week: period === Period.Week ? 'numeric' : undefined,
      weekday: period === Period.Weekday ? 'short' : undefined,
      timeZone: 'UTC'
    };
  }

  public format(index: number, value: any): string {
    if (value === null || value === undefined) {
      return this.noValueText;
    }

    if (index >= this.formatters.length) {
      return value.toString();
    }

    return this.formatters[index]?.(value) || value.toString();
  }

  public hasCategories(axisColIndex: number): boolean {
    if (axisColIndex < 0) {
      return false;
    }
    const col = this.dataModel.columns[axisColIndex];
    return !(col.dataType === 'Date'
      || (col.dataType === 'Float' && col.interval == null && col.codetable == null)
      || ((col.dataType === 'Integer' || col.dataType === 'BigInteger') &&
        col.interval == null && col.codetable == null && col.period == null))
      || (col.period === Period.Weekday) || (col.period === Period.Week) || (col.domainNode === 357);
  }

  public hasIcons(colIndex: number) {
    return this.columnMetaData[colIndex].hasIcon;
  }

  public hasBgColors(colIndex: number) {
    return this.columnMetaData[colIndex].hasBgColour;
  }

  public hasFgColors(colIndex: number) {
    return this.columnMetaData[colIndex].hasFgColour;
  }

  public isAggrColumn(columns: FavouriteDataColumn[], index: number): boolean {
    const col = columns[index];
    return col.columnType === 'aggregation' || col.columnType === 'aggregationRollAvg' || col.columnType === 'target';
  }

  public columnType(columnIndex: number) {
    const col = this.dataModel.columns[columnIndex];
    if (col) {
      return col.columnType;
    }

    return undefined;
  }
}
